import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronLeft, Check, X, AlertTriangle, Apple, MessageCircle, Loader2 } from "lucide-react";
import { api, openWhatsApp } from "@/lib/api";

export default function HealthGuideDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/guides/${id}`).then((r) => setGuide(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) return <div className="p-6 text-slate-500">Guide not found.</div>;
  if (!guide) return <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  const Icon = Icons[guide.icon] || Icons.HeartPulse;

  const consult = () => {
    openWhatsApp(`Hello U&URS NUTRITIONIST 👋\n\nI read the *${guide.title}* diet guide and would like a personalized plan. Please guide me.`);
  };

  return (
    <div className="animate-fade-in pb-28">
      <div className="relative px-5 pt-4 pb-6 bg-emerald-800 dark:bg-emerald-900">
        <button
          data-testid="guide-back-btn"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center">
            <Icon className="w-7 h-7 text-emerald-200" />
          </div>
          <div>
            <h1 className="text-lg font-extrabold text-white font-head leading-snug">{guide.title}</h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-emerald-100/80 leading-relaxed">{guide.description}</p>
      </div>

      {guide.image && (
        <div className="px-5 pt-5">
          <img
            data-testid="guide-detail-image"
            src={guide.image}
            alt={guide.title}
            className="w-full h-auto object-contain rounded-2xl"
          />
        </div>
      )}

      <div className="px-5 pt-5 space-y-5">
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-100 dark:border-emerald-800">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center"><Check className="w-4 h-4 text-white" /></span>
            <h3 className="text-sm font-bold text-emerald-800 dark:text-emerald-300">Do's</h3>
          </div>
          <ul className="space-y-2">
            {guide.dos.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {d}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg bg-rose-500 flex items-center justify-center"><X className="w-4 h-4 text-white" /></span>
            <h3 className="text-sm font-bold text-rose-700 dark:text-rose-300">Don'ts</h3>
          </div>
          <ul className="space-y-2">
            {guide.donts.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                <X className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" /> {d}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <Apple className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-sm font-bold text-emerald-950 dark:text-emerald-50">Recommended Foods</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {guide.recommended_foods.map((f) => (
              <span key={f} className="text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-800 px-3 py-1.5 rounded-full">{f}</span>
            ))}
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Dietary Caution</h3>
          </div>
          <p className="text-sm text-amber-800/80 dark:text-amber-200/70 leading-relaxed">{guide.cautions}</p>
        </div>

        <button
          data-testid="guide-consult-btn"
          onClick={consult}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-2xl transition-colors shadow-lg shadow-emerald-600/20"
        >
          <MessageCircle className="w-5 h-5" /> Get Personalized Plan
        </button>
      </div>
    </div>
  );
}
